import { useEffect, useRef, useState } from "react";
import { MessageCircle, Link2, ArrowRight } from "lucide-react";

/**
 * A simulated external-channel conversation (Messenger-style): the one
 * screen in this walkthrough that's intentionally NOT the real ConfirmPH UI,
 * because it represents a channel outside ConfirmPH. The walkthrough
 * revisits this SAME conversation three times (inquiry, confirmation link,
 * review link): `staticMessages` is prior history rendered instantly with
 * no animation ("do not replay old messages"), `script` is only the new
 * message(s) for the current visit, and `linkCard`/`continueLabel` is
 * whatever the visit ends on (a tappable link, or a plain Continue when
 * there's no link yet).
 */
export default function ChatSimulation({
  staticMessages = [],
  script = [],
  linkCard = null,
  continueLabel = null,
  onLinkClick,
  onContinue,
  autoPlay = true,
  reducedMotion = false,
  alreadyPlayed = false,
  onFullyPlayed,
  channelLabel = "Messenger · simulated, external to ConfirmPH",
}) {
  const effectiveReduced = reducedMotion || alreadyPlayed;
  const [visibleCount, setVisibleCount] = useState(effectiveReduced ? script.length : 0);
  const [typingFrom, setTypingFrom] = useState(null);
  const [ready, setReady] = useState(effectiveReduced);
  const timers = useRef([]);

  useEffect(() => {
    if (effectiveReduced || !autoPlay) { setReady(true); setVisibleCount(script.length); return; }
    let cancelled = false;
    let t = 400;
    script.forEach((msg, i) => {
      timers.current.push(setTimeout(() => { if (!cancelled) setTypingFrom(msg.from); }, t));
      t += 700;
      timers.current.push(setTimeout(() => {
        if (cancelled) return;
        setTypingFrom(null);
        setVisibleCount(i + 1);
      }, t));
      t += 900;
    });
    // A brief "business is typing" beat before the link/continue affordance
    // appears: matters most when `script` is empty (a revisit that only
    // has a new link to reveal) so it doesn't just pop in instantly.
    if (linkCard) {
      timers.current.push(setTimeout(() => { if (!cancelled) setTypingFrom("business"); }, t));
      t += 900;
    }
    timers.current.push(setTimeout(() => { if (!cancelled) { setTypingFrom(null); setReady(true); } }, t + 300));
    return () => { cancelled = true; timers.current.forEach(clearTimeout); timers.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveReduced, autoPlay]);

  useEffect(() => { if (ready) onFullyPlayed?.(); }, [ready, onFullyPlayed]);

  function skip() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setTypingFrom(null);
    setVisibleCount(script.length);
    setReady(true);
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="walkthrough-chat">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          <MessageCircle className="w-3.5 h-3.5" /> {channelLabel}
        </div>
        {!ready && (
          <button type="button" onClick={skip} data-testid="walkthrough-chat-skip"
                  className="text-xs text-slate-500 hover:text-slate-800 underline">
            Skip
          </button>
        )}
      </div>

      <div className="space-y-2.5 min-h-[180px]" aria-live="polite">
        {staticMessages.map((msg, i) => (
          <ChatBubble key={`static-${i}`} from={msg.from} text={msg.text} />
        ))}
        {script.slice(0, visibleCount).map((msg, i) => (
          <ChatBubble key={`new-${i}`} from={msg.from} text={msg.text} />
        ))}
        {typingFrom && <TypingBubble from={typingFrom} />}
        {ready && linkCard && (
          <div className="flex justify-start">
            <button
              type="button"
              onClick={onLinkClick}
              data-testid="walkthrough-chat-link-card"
              className="max-w-[85%] rounded-2xl rounded-bl-sm bg-slate-100 hover:bg-slate-200 px-3.5 py-2 text-left text-sm text-slate-800 transition-colors"
            >
              <span className="text-slate-500">ConfirmPH:</span> {linkCard.prefix}{" "}
              <span className="text-[#1B4332] underline inline-flex items-center gap-0.5">
                {linkCard.linkText} <Link2 className="w-3 h-3" />
              </span>
            </button>
          </div>
        )}
        {ready && !linkCard && continueLabel && (
          <div className="flex justify-center pt-1">
            <button
              type="button"
              onClick={onContinue}
              data-testid="walkthrough-chat-continue"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-full border border-slate-300 hover:bg-slate-50"
            >
              {continueLabel} <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatBubble({ from, text }) {
  const isCustomer = from === "customer";
  return (
    <div className={`flex ${isCustomer ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
          isCustomer ? "bg-[#1B4332] text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"
        }`}
      >
        {text}
      </div>
    </div>
  );
}

function TypingBubble({ from }) {
  const isCustomer = from === "customer";
  return (
    <div className={`flex ${isCustomer ? "justify-end" : "justify-start"}`} data-testid="walkthrough-chat-typing">
      <div className={`rounded-2xl px-3.5 py-2.5 ${isCustomer ? "bg-[#1B4332]" : "bg-slate-100"} ${isCustomer ? "rounded-br-sm" : "rounded-bl-sm"}`}>
        <span className="inline-flex gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`w-1.5 h-1.5 rounded-full ${isCustomer ? "bg-white/70" : "bg-slate-400"} animate-bounce motion-reduce:animate-none`}
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}
